import { Button, Dropdown, Option, Checkbox, Field, Input, Menu, MenuTrigger, MenuList, MenuItem, MenuPopover, MenuButton } from "@fluentui/react-components";
import { DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, TableColumnDefinition, createTableColumn } from "@fluentui/react-table";
import "./styles.scss";
import LoadingWrapper from "#root/components/LoadingWrapper";
import Paginator from "#root/components/Paginator";
import { ROUTES } from "#root/routes/constants";
import { AddRegular, DeleteRegular, EditRegular, CheckmarkRegular, DismissRegular, PersonRegular, LockClosedRegular } from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useBatchResetPassword, useBatchUpdateRole, User } from "#root/queries/users";
import React, { useState } from "react";
import { useBatchDeleteUsers, useUsers, useUpdateUser } from "#root/queries/users";
import { showToast } from "#root/components/GlobalToaster";
import { ROLES } from "#root/utils/roles";
import { DialogBox } from "#root/components/DialogBox";
import { toSentenceCase } from "#root/services/utils";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { useCurrentUser } from "#root/queries/local";
import { useDispatch } from "react-redux";
import { logout } from "#root/redux/auth/actions";
import { VALIDATION_MESSAGES, VALIDATION_PATTERNS } from "#root/utils/validation";
import ButtonGroup from "#root/components/ButtonGroup";
import { useRequestResetPassword } from "#root/queries/auth";

const UsersAdministration: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { data: currentUser } = useCurrentUser();
    const [currentPage, setCurrentPage] = useState(1);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [usersToDelete, setUsersToDelete] = useState<User[]>([]);
    const [usersToResetPassword, setUsersToResetPassword] = useState<User[]>([]);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editedValues, setEditedValues] = useState<User>({});
    const [isUpdateRoleDialogOpen, setIsUpdateRoleDialogOpen] = useState(false);
    const [newRole, setNewRole] = useState('');
    const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [passwordManual, setPasswordManual] = useState(true);

    const {
        data: usersResponse,
        isLoading,
        refetch
    } = useUsers({
        skip: (currentPage - 1) * itemsPerPage,
        take: itemsPerPage
    });

    const updateUserMutation = useUpdateUser();
    const batchDeleteUsersMutation = useBatchDeleteUsers();
    const batchResetPasswordMutation = useBatchResetPassword();
    const batchUpdateRoleMutation = useBatchUpdateRole();
    const requestPasswordResetMutation = useRequestResetPassword();

    const totalCount = usersResponse?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const users = usersResponse?.items ?? [];

    // Create handling

    const handleCreate = (multiple: boolean) => {
        if (multiple) {
            navigate(`${ROUTES.ORGANIZATION_SETTINGS.ADD_USER}?multiple=true`);
        } else {
            navigate(ROUTES.ORGANIZATION_SETTINGS.ADD_USER);
        }
    };


    // Edit handling

    const handleEdit = (user?: User) => {
        if (user) {
            setEditingUser(user);
            setEditedValues(user);
        }
    };

    const handleEditConfirm = async () => {
        if (editingUser?.id && editedValues) {
            try {
                if (editedValues !== editingUser) {
                    await updateUserMutation.mutateAsync({
                        id: editingUser.id,
                        user: editedValues
                    });
                    showToast('User updated successfully', 'success');
                }
                setEditingUser(null);
                setEditedValues({});
            }
            catch (error) {
                showToast('Failed to update user', 'error');
            }
        }
    };

    // Delete handling

    const handleDeleteClick = (user: User, e?: React.MouseEvent) => {
        e?.stopPropagation();
        setUsersToDelete([user]);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteMultipleClick = () => {
        const usersToDelete = users.filter(user => user.id && selectedUsers.has(user.id));
        setUsersToDelete(usersToDelete);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await batchDeleteUsersMutation.mutateAsync({ ids: usersToDelete.map(user => user.id!) });
            setIsDeleteDialogOpen(false);
            showToast(`${usersToDelete.length > 1 ? 'Users' : 'User'} deleted successfully`, 'success');
            
            // Check if current user is among deleted users
            const isCurrentUserDeleted = usersToDelete.some(user => user.id === currentUser?.id);
            
            setSelectedUsers(new Set());
            setUsersToDelete([]);
            
            if (isCurrentUserDeleted) {
                showToast('Your account has been deleted. You are being logged out.', 'info');
                setTimeout(() => {
                    dispatch(logout());
                    navigate(ROUTES.LOGIN);
                }, 500);
            } else {
                refetch();
            }
        } catch (error) {
            showToast(`Failed to delete ${usersToDelete.length > 1 ? 'users' : 'user'}`, 'error');
        }
    };

    // Update role handling

    const handleUpdateRoleConfirm = async () => {
        try {
            await batchUpdateRoleMutation.mutateAsync({
                ids: Array.from(selectedUsers),
                role: newRole
            });
            showToast('Users roles updated successfully', 'success');
            setIsUpdateRoleDialogOpen(false);
            setSelectedUsers(new Set());
            setNewRole('');
            refetch();
        } catch (error) {
            showToast('Failed to update users roles', 'error');
        }
    };

    // Reset password handling

    const handlePasswordModeChange = (value: boolean) => {
        setPasswordManual(value);
        setNewPassword('');
    };

    const handleResetPasswordClick = (user: User, e?: React.MouseEvent) => {
        e?.stopPropagation();
        setNewPassword('');
        setUsersToResetPassword([user]);
        setIsResetPasswordDialogOpen(true);
    };

    const handleResetPasswordMultipleClick = () => {
        const usersToResetPassword = users.filter(user => user.id && selectedUsers.has(user.id));
        setNewPassword('');
        setUsersToResetPassword(usersToResetPassword);
        setIsResetPasswordDialogOpen(true);
    };

    const handleResetPasswordConfirm = async () => {
        try {
            if (passwordManual) {
                await batchResetPasswordMutation.mutateAsync({
                    ids: usersToResetPassword.map(user => user.id!),
                    password: newPassword
                });
                showToast('Passwords reset successfully', 'success');
            } else {
                await Promise.all(
                    usersToResetPassword.map(user => 
                        requestPasswordResetMutation.mutateAsync({
                            email: user.email!
                        })
                    )
                );
                showToast('Password reset emails sent successfully', 'success');
            }
            
            setIsResetPasswordDialogOpen(false);
            setSelectedUsers(new Set());
            setNewPassword('');
        } catch (error) {
            showToast('Failed to reset passwords', 'error');
        }
    };

    // Pagination handling

    const handleItemsPerPageChange = (newItemsPerPage: number): void => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        refetch();
    };

    // Handle selection

    const onSelectionChange = (event: React.MouseEvent | React.KeyboardEvent, data: any) => {
        // Only allow selection via checkbox click
        const target = event.target as HTMLElement;
        if (target.closest('[type="checkbox"]')) {
            setSelectedUsers(new Set(data.selectedItems));
        }
    };

    const getSelectionState = () => {
        if (selectedUsers.size === 0) return false;
        if (selectedUsers.size === users.length) return true;
        return 'mixed';
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedUsers(new Set(users.map(user => user.id!)));
        } else {
            setSelectedUsers(new Set());
        }
    };

    const formatRoleLabel = (role: string) => {
        return toSentenceCase(role);
    };

    const availableRoles = Object.values(ROLES).filter(role => role !== ROLES.GLOBALADMIN);

    const columns: TableColumnDefinition<User>[] = [
        createTableColumn<User>({
            columnId: "firstName",
            renderHeaderCell: () => {
                return "First Name";
            },
            renderCell: (item) => {
                return (
                    <>
                        {editingUser?.id === item.id ? (
                            <Input
                                appearance="underline"
                                value={editedValues.firstName || ''}
                                onChange={(_, data) => setEditedValues(prev => ({ ...prev, firstName: data.value }))}
                                disabled={updateUserMutation.isPending}
                            />
                        ) : (
                            <div title={item.firstName}>{item.firstName}</div>
                        )}
                    </>
                );
            },
        }),
        createTableColumn<User>({
            columnId: "lastName",
            renderHeaderCell: () => {
                return "Last Name";
            },
            renderCell: (item) => {
                return (
                    <>
                        {editingUser?.id === item.id ? (
                            <Input
                                appearance="underline"
                                value={editedValues.lastName || ''}
                                onChange={(_, data) => setEditedValues(prev => ({ ...prev, lastName: data.value }))}
                                disabled={updateUserMutation.isPending}
                            />
                        ) : (
                            <div title={item.lastName}>{item.lastName}</div>
                        )}
                    </>
                );
            },
        }),
        createTableColumn<User>({
            columnId: "email",
            renderHeaderCell: () => {
                return "Email";
            },
            renderCell: (item) => {
                return (
                    <>
                        {editingUser?.id === item.id ? (
                            <Input
                                appearance="underline"
                                value={editedValues.email || ''}
                                onChange={(_, data) => setEditedValues(prev => ({ ...prev, email: data.value }))}
                                disabled={updateUserMutation.isPending}
                            />
                        ) : (
                            <div title={item.email}>{item.email}</div>
                        )}
                    </>
                );
            },
        }),
        createTableColumn<User>({
            columnId: "phoneNumber",
            renderHeaderCell: () => {
                return "Phone";
            },
            renderCell: (item) => {
                return (
                    <>
                        {editingUser?.id === item.id ? (
                            <Input
                                appearance="underline"
                                value={editedValues.phoneNumber || ''}
                                onChange={(_, data) => setEditedValues(prev => ({ ...prev, phoneNumber: data.value }))}
                                disabled={updateUserMutation.isPending}
                            />
                        ) : (
                            <div title={item.phoneNumber}>{item.phoneNumber}</div>
                        )}
                    </>
                );
            },
        }),
        createTableColumn<User>({
            columnId: "role",
            renderHeaderCell: () => {
                return "Role";
            },
            renderCell: (item) => {
                return (
                    <>
                        {editingUser?.id === item.id ? (
                            <Dropdown
                                appearance="underline"
                                value={formatRoleLabel(editedValues.role || '')}
                                onOptionSelect={(_, data) => setEditedValues(prev => ({ ...prev, role: data.optionValue || '' }))}
                                disabled={updateUserMutation.isPending}
                            >
                                {availableRoles.map((role) => (
                                    <Option key={role} value={role}>
                                        {formatRoleLabel(role)}
                                    </Option>
                                ))}
                            </Dropdown>
                        ) : (
                            <div title={item.role}>{item.role}</div>
                        )}
                    </>
                );
            },
        }),
        createTableColumn<User>({
            columnId: "actions",
            renderHeaderCell: () => {
                return "Actions";
            },
            renderCell: (item) => {
                return (
                    editingUser?.id === item.id ? (
                        <>
                            <Button
                                appearance="primary"
                                icon={<CheckmarkRegular />}
                                onClick={() => handleEditConfirm()}
                                disabled={updateUserMutation.isPending}
                            />
                            <Button
                                appearance="secondary"
                                icon={<DismissRegular />}
                                onClick={() => setEditingUser(null)}
                                disabled={updateUserMutation.isPending}
                            />
                        </>
                    ) : (
                        <>
                            <Button
                                title="Edit User"
                                appearance="subtle"
                                icon={<EditRegular />}
                                onClick={() => handleEdit(item)}
                                disabled={updateUserMutation.isPending}
                            />
                            <Button
                                title="Reset Password"
                                appearance="subtle"
                                icon={<LockClosedRegular />}
                                onClick={(e) => handleResetPasswordClick(item, e)}
                                disabled={batchDeleteUsersMutation.isPending}
                            />
                            <Button
                                title="Delete User"
                                appearance="subtle"
                                icon={<DeleteRegular />}
                                onClick={(e) => handleDeleteClick(item, e)}
                                disabled={batchDeleteUsersMutation.isPending}
                            />
                        </>
                    )
                )
            },
        }),
    ];

    return (
        <div className="users-administration">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>Users Administration <div className="divider" />
                        <Button
                            appearance="primary"
                            icon={<AddRegular />}
                            onClick={() => handleCreate(false)}
                        >
                            New User
                        </Button>
                        <Button
                            appearance="primary"
                            icon={<AddRegular />}
                            onClick={() => handleCreate(true)}
                        >
                            Add Multiple Users
                        </Button>
                    </h2>
                </div>
            </div>

            <LoadingWrapper
                isLoading={isLoading}
                isEmpty={users.length === 0}
                emptyMessage="No users found."
                emptyIcon={<PersonRegular />}
            >
                <div className="emc-grid">
                    <DataGrid
                        className="emc-datagrid"
                        items={users}
                        columns={columns}
                        selectionMode="multiselect"
                        selectedItems={selectedUsers}
                        onSelectionChange={onSelectionChange}
                        getRowId={(item) => item.id!}
                        focusMode="none"
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {({ renderHeaderCell, columnId }) => (
                                    <DataGridHeaderCell className={columnId.toString()}>
                                        {renderHeaderCell()}
                                    </DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<User>>
                            {({ item, rowId }) => (
                                <DataGridRow<User>
                                    key={rowId}
                                    selectionCell={{ "aria-label": "Select row" }}
                                    className={editingUser?.id === item.id ? 'editing' : ''}
                                >
                                    {({ renderCell, columnId }) => (
                                        <DataGridCell className={columnId.toString()}>
                                            {renderCell(item)}
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>

                    <Paginator
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalCount}
                        currentItems={(currentPage - 1) * itemsPerPage + 1}
                        showItemsPerPage={true}
                    />
                </div>
            </LoadingWrapper>

            {selectedUsers.size > 0 && (
                <div className="emc-page-footer">
                    <Checkbox
                        checked={getSelectionState()}
                        onChange={(_, data) => handleSelectAll(!!data.checked)}
                        label="Select All"
                    />
                    <Button
                        appearance="secondary"
                        onClick={() => setSelectedUsers(new Set())}
                    >
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        icon={<DeleteRegular />}
                        onClick={handleDeleteMultipleClick}
                    >
                        Delete Selected
                    </Button>
                    <Menu>
                        <MenuTrigger>
                            <MenuButton
                                appearance="primary"
                                icon={<PersonRegular />}
                            >
                                Update Role
                            </MenuButton>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                {availableRoles.map((role) => (
                                    <MenuItem
                                        key={role}
                                        onClick={() => {
                                            setNewRole(role);
                                            setIsUpdateRoleDialogOpen(true);
                                        }}
                                    >
                                        {formatRoleLabel(role)}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    <Button
                        appearance="primary"
                        icon={<LockClosedRegular />}
                        onClick={handleResetPasswordMultipleClick}
                    >
                        Reset Password
                    </Button>
                </div>
            )}

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                title={`Delete ${usersToDelete.length > 1 ? 'Users' : 'User'}`}
                primaryButtonText="Delete"
                onPrimaryButtonClick={handleConfirmDelete}
                isPrimaryButtonLoading={batchDeleteUsersMutation.isPending}
            >
                Permanently delete {usersToDelete.length === 1 ? `user ${usersToDelete[0]?.email}` : `${usersToDelete.length} users`}?
                You can't undo this action.
            </DialogBox>

            <DialogBox
                open={isUpdateRoleDialogOpen}
                onOpenChange={setIsUpdateRoleDialogOpen}
                title="Update Role"
                primaryButtonText="Confirm"
                onPrimaryButtonClick={handleUpdateRoleConfirm}
                isPrimaryButtonLoading={batchUpdateRoleMutation.isPending}
            >
                Update role to {formatRoleLabel(newRole)} for {selectedUsers.size === 1 ? `selected user` : `${selectedUsers.size} users`}?
            </DialogBox>

            <DialogBox
                open={isResetPasswordDialogOpen}
                onOpenChange={setIsResetPasswordDialogOpen}
                title="Reset Password"
                primaryButtonText="Confirm"
                onPrimaryButtonClick={handleResetPasswordConfirm}
                isPrimaryButtonLoading={batchResetPasswordMutation.isPending || requestPasswordResetMutation.isPending}
                isPrimaryButtonDisabled={passwordManual && !newPassword.trim()}
            >
                <form>
                    <Field label="New Password" required hint={passwordManual ? VALIDATION_MESSAGES.PASSWORD : ""}>
                        <ButtonGroup
                            name="password-mode"
                            options={[
                                { label: 'Manual', value: true },
                                { label: 'Send Reset Link', value: false }
                            ]}
                            onChange={handlePasswordModeChange}
                            value={passwordManual}
                            required
                            className="password-button-group"
                        />
                        {passwordManual && (
                            <Input className="password-input"
                                autoComplete="new-password"
                                name="new-password"
                                //type="password"
                                value={newPassword}
                                onChange={(_, data) => setNewPassword(data.value)}
                                pattern={VALIDATION_PATTERNS.PASSWORD}
                                title={VALIDATION_MESSAGES.PASSWORD}
                                required
                            />
                        )}
                    </Field>
                </form>
            </DialogBox>
        </div>
    );
};

export default UsersAdministration;